import { ValidatorFn } from '@angular/forms';
import { BatchDomain, CtBatchSource, DocumentClassKey } from '@ps-gui/claims-terminal-shared';
import { associateBy, FieldType, mapValues, Override } from '@ps-gui/shared-utils';
import {
  ExtractionConfiguration,
  ExtractionConfigurationByDomain,
  ExtractionPage,
  ExtractionSection,
  isFieldGroupModel,
  isTableModel,
} from '../domain-extraction-specifications';
import { ExtractedRow, ExtractedValue } from '../extracted-values';
import { ExtractedRowForm } from '../extraction-form-models';
import { proofConfig } from './proof/proof-config';
import { Kopfdaten } from './proof/kopfdaten-extraction-model';
import { DropdownOption, ValidationConfig } from '@ps-gui/ui';

// These types are useful to simplify the readers "parsing" of record types:
// For example, it is much easier to understand what
// `Record<TableKey, something>` stores, compared to `Record<string, something>`
// ("what is this `string` here again? Let me check the name and usages...")
//
// Of course, this is not a replacement for good naming: In the situation above
// it is still best to name the record `somethingByTableKey`.
export type FieldGroupKey = string;

export type TableKey = string;
export type FieldModelKey = string;
export type TableColumnKey = FieldModelKey;
/// ----------------------------------------------

/**
 * Type of the group
 *  - simple: just holds a list of fields
 *  - binary: holds two different lists and determines which is shown
 *  - multi: holds more than two lists and determines which is shown
 */
export type SimpleGroupType = 'simple';
export type BinaryGroupType = 'binary';
export type MultiGroupType = 'multi';
export type GroupType = SimpleGroupType | BinaryGroupType | MultiGroupType;

export type IndexedFieldModelDict<T extends Record<string, ExtractedValue>> = {
  [K in keyof T]: FieldModel<K, T[K]>;
};

export type MultiGroupEntry<T extends Record<string, ExtractedValue>> = {
  label: string;
  fieldModels: IndexedFieldModelDict<T>;
};

export interface GermanWord {
  text: string;
  genus: 'M' | 'F' | 'N';
}

export type BaseFieldModel<K = string, T = ExtractedValue> = {
  key: never extends K ? string : K;
  label: GermanWord;
  type: T extends string
    ?
        | FieldType.STRING
        | FieldType.DATE
        | FieldType.TIME
        | FieldType.DROPDOWN
        | FieldType.RADIO
        | FieldType.TEXTAREA
    : T extends number
      ? FieldType.NUMBER | FieldType.CURRENCY
      : never;
  validators?: ValidationConfig[];
  widthUtilityClass?: string;
  defaultValue?: T;
  onlyVisibleInConsolidation?: boolean;
  visibleForSource?: CtBatchSource[];
  disabled?: boolean;

  /**
   * This property has nothing to do with the normal "consolidation" step. It marks
   * properties which should NOT be emitted from extraction pages because they are only
   * used to perform "inline consolidation". In it we store the name of the field
   * this field model is a consolidator for.
   */
  inlineConsolidatorForField?: string;
  inlineConsolidatorRequired?: boolean;
  transformConsolidationValue?: (value: string) => string;
  customTransformValue?: (value: string) => T;
  index: number;
};

export type DropdownFieldModel<K = string, T = ExtractedValue> = Override<
  BaseFieldModel<K, T>,
  {
    type: FieldType.DROPDOWN;
    defaultValue?: string | { label: string; value: string };
  }
> & {
  options: string[] | { label: string; value: string }[];
};

export type RadioFieldModel<K = string, T = ExtractedValue> = Override<
  BaseFieldModel<K, T>,
  {
    type: FieldType.RADIO;
  }
> & {
  options: string[];
};

export type FieldModel<K = string, T = ExtractedValue> =
  | BaseFieldModel<K, T>
  | DropdownFieldModel<K, T>
  | RadioFieldModel<K, T>;

export interface FieldGroupModel<T extends readonly FieldModel[] = readonly FieldModel[]> {
  key: string;
  label: string;
  icon?: string;
  fieldModels: T;
  groupValidators?: ValidatorFn[];
  index: number;
  type: 'fieldGroupModel';
  visibleForSource?: CtBatchSource[];
  invisibleInConsolidation?: boolean;
}

export type TypedFieldGroupModel<T extends readonly FieldModel[]> = Override<
  FieldGroupModel<T>,
  {
    type: GroupType;
  }
>;

export type SimpleFieldGroupModel<T extends readonly FieldModel[]> = Override<
  TypedFieldGroupModel<T>,
  { type: SimpleGroupType }
>;

export type BinaryFieldGroupModel<T extends FieldModel[]> = Override<
  TypedFieldGroupModel<T>,
  {
    type: BinaryGroupType;
    fieldModels: {
      true: IndexedFieldModelDict<never>;
      false: IndexedFieldModelDict<never>;
    };
  }
>;
export type MultiFieldGroupModel<T extends readonly FieldModel[]> = Override<
  TypedFieldGroupModel<T>,
  {
    type: MultiGroupType;
    fieldModels: {
      [k: string]: MultiGroupEntry<never>;
    };
  }
>;

/**
 * Specifies the structure of table sections. The underlying
 * field group sections defines the structure of each table row.
 */
export type TableModel<
  T extends readonly FieldModel[] = FieldModel[],
  Q extends readonly T[number][] = T,
> = Omit<FieldGroupModel<T>, 'type'> & {
  rowClassification?: {
    classLabel: string;
    classColumnWidthUtilityClass: string;
    classes: Record<string, string>;
    visibleFieldModels: Q;
    callbackProvider: (extractedRowForm: ExtractedRowForm, classValue: string | undefined) => void;
    tableCallbackProvider?: (
      extractedRowForms: ExtractedRowForm[],
      currentRowForm: ExtractedRowForm,
      classValue: string | undefined,
    ) => void;
    initialValueProvider: (
      extractedRowData: ExtractedRow['rowData'],
      fallBackValue: ExtractedRow['class'],
      rowIndex: number,
    ) => string | undefined;
  };
  tableValidators?: ValidatorFn[];
  type: 'tableModel';
};

export const ct2ConfigurationByBatchDomain = {
  [BatchDomain.PROOF]: proofConfig,
} as const satisfies ExtractionConfigurationByDomain;

const required = <T>(value: T | null | undefined, message: string): T => {
  if (value == null) throw new Error(message);
  return value;
};

export const sectionsByDomain = mapValues(ct2ConfigurationByBatchDomain, (_, config) =>
  Object.values(config.extractionConfigurationByClass)
    .flatMap<ExtractionPage>((config) => config.extractionPages)
    .flatMap<ExtractionSection>((page: ExtractionPage) => page.sections),
);

export const sectionsByDomainAndDocumentClass = mapValues(
  ct2ConfigurationByBatchDomain,
  (_, config) =>
    mapValues<DocumentClassKey, ExtractionConfiguration, ExtractionSection[]>(
      config.extractionConfigurationByClass,
      (_, extractionConfig) => extractionConfig.extractionPages.flatMap((page) => page.sections),
    ),
);

export const consolidationSections = (domain: BatchDomain) => {
  const config = ct2ConfigurationByBatchDomain[domain];

  return Object.entries(config.extractionConfigurationByClass)
    .filter(([documentClass, _]) =>
      config.consolidation.consolidatedDocumentClasses.includes(documentClass as DocumentClassKey),
    )
    .flatMap<ExtractionPage>(([_, classConfig]) => classConfig.extractionPages)
    .flatMap((page) => page.sections);
};

const fieldsByKeyByDomain = mapValues(sectionsByDomain, (_, sections) => ({
  ...associateBy(
    sections.filter(isFieldGroupModel).flatMap((section) => section.fieldModels),
    (section) => section.key,
  ),
  // this is the only field not in a section right now, so we just hardcode it here for now.
  [Kopfdaten.kopfdaten_gesamtbetrag.key]: Kopfdaten.kopfdaten_gesamtbetrag,
}));

export const getFieldModels = (domain: BatchDomain) => Object.values(fieldsByKeyByDomain[domain]);

export const getFieldModel = (domain: BatchDomain, fieldKey: string): FieldModel =>
  required(
    fieldsByKeyByDomain[domain][fieldKey],
    `Field with key '${fieldKey}' not found in domain '${domain}'`,
  );

export const fieldGroupSectionsByDomain = mapValues(sectionsByDomain, (_, sections) =>
  sections.filter((section) => isFieldGroupModel(section)),
);

export const fieldGroupSectionsByDomainAndDocumentClass = mapValues(
  ct2ConfigurationByBatchDomain,
  (_, sections) => sections.extractionConfigurationByClass,
);

export const tableSectionsByDomain = mapValues(sectionsByDomain, (_, sections) =>
  sections.filter((section) => isTableModel(section)),
);

export const tableSectionsByDomainAndDocumentClass = mapValues(
  ct2ConfigurationByBatchDomain,
  (_, sections) => sections.extractionConfigurationByClass,
);

export const getTableModel = (domain: BatchDomain, tableKey: TableKey) =>
  tableSectionsByDomain[domain].find((model) => model.key === tableKey);

export const getFieldGroupKeyByFieldKey = (domain: BatchDomain) =>
  Object.fromEntries(
    fieldGroupSectionsByDomain[domain].flatMap((fieldGroupModel) =>
      fieldGroupModel.fieldModels.map((fieldModel) => [fieldModel.key, fieldGroupModel.key]),
    ),
  );

export const getTableModelByColumnKey = (domain: BatchDomain, columnFieldKey: FieldModelKey) =>
  tableSectionsByDomain[domain].find((model) =>
    model.fieldModels.some((fieldModel) => fieldModel.key === columnFieldKey),
  );

export function toDropdownOption(value: string): DropdownOption<string> {
  return {
    value,
    label: value,
  };
}

export function toDropdownOptions(values: string[]): DropdownOption<string>[] {
  return values.map((v) => toDropdownOption(v));
}

export function createDocumentDataFieldGroup(): FieldGroupModel {
  return {
    key: 'dokumentendaten',
    label: 'Dokumentendaten',
    index: 0,
    fieldModels: [
      {
        key: 'document_creation_date',
        label: {
          text: 'Erstelldatum',
          genus: 'N',
        },
        type: FieldType.DATE,
        index: 0,
      },
    ],
    type: 'fieldGroupModel',
    invisibleInConsolidation: true,
  };
}
